<template>
  <v-carousel-item
    v-if="!disabled"
    transition="carousel-form-slide"
    reverse-transition="carousel-form-slide-reverse"
  >
    <div
      :class="`fill-height d-flex flex-column justify-space-between px-4 px-md-8 pt-10 pb-14 form-carousel-item-${name}`"
      style="margin-top: 0 !important; margin-bottom: 0 !important; overflow-y: hidden"
    >
      <div class="d-inline-block w-100 flex-grow-1">
        <label class="text-h6 pb-4">
          <span v-if="label">{{ label }}</span>
          <slot v-else name="label" />
        </label>
        <v-row no-gutters class="my-10">
          <v-col cols="12" :md="fillAllCols ? 12 : 7">
            <slot />
          </v-col>
        </v-row>
      </div>

      <div class="d-inline-block w-100 flex-grow-0 d-flex flex-column">
        <FormButtons
          :disabled="disableNextIf"
          :next-label="item?.isLast ? t('form.go') : t('form.next')"
          :next-loading="nextLoading"
          :enter-submit-enabled="enterSubmit"
          :previous-label="!item?.isFirst && !noBackButton ? t('form.back') : undefined"
          :no-next-button="noNextButton"
          @next-click="handleNextClick"
          @previous-click="handlePreviousClick"
        />
        <div v-if="!carouselContext?.hideProgress" class="mt-4 d-flex ga-2 ga-md-8">
          <v-progress-linear
            v-for="i in totalSteps"
            :key="i"
            :model-value="i === currentStep + 1 ? 100 : 0"
            color="purple-4"
            height="2"
            rounded="pill"
          />
        </div>
      </div>
    </div>
  </v-carousel-item>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from "uuid";
import { computed, inject, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CarouselContext } from "@/components/Form/FormCarousel.vue";

const { t } = useI18n();

const carouselContext = inject<CarouselContext>("carouselContext");

const itemId = uuidv4();

const { name, disabled, disableNextIf, nextLoading, autoNext, noNextButton, enterSubmit } =
  defineProps({
    name: {
      type: String,
      required: false,
      default: undefined,
    },
    label: {
      type: String,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableNextIf: {
      type: Boolean,
      required: false,
      default: false,
    },
    nextLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    autoNext: {
      type: Boolean,
      required: false,
      default: true,
    },
    fillAllCols: {
      type: Boolean,
      required: false,
      default: false,
    },
    noNextButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    noBackButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    enterSubmit: {
      type: Boolean,
      required: false,
      default: true,
    },
  });

const isLoaded = ref(false);

const item = computed(() => carouselContext?.items.find((item) => item.id === itemId));

const nextSlideIndex = computed(() => (item.value?.index != null ? item.value.index + 1 : 0));
const previousSlideIndex = computed(() => (item.value?.index != null ? item.value.index - 1 : 0));

const emit = defineEmits(["focus", "next"]);

const scrollTo = (position: number) => {
  const element = document.querySelector(`.form-carousel-item-${name}`);
  if (element) {
    element.scrollTo({ top: position, behavior: "instant" });
  }
};

const handleNextClick = () => {
  if (!disableNextIf && isLoaded.value) {
    if (autoNext && !item?.value?.isLast && !noNextButton) {
      carouselContext?.goToItem(nextSlideIndex.value);
      nextTick(() => scrollTo(carouselContext ? carouselContext.scrollY - 10 : 0));
    }
    emit("next");
  }
};

const handlePreviousClick = () => {
  carouselContext?.goToItem(previousSlideIndex.value);
  nextTick(() => scrollTo(carouselContext ? carouselContext.scrollY - 10 : 0));
};

watch(
  () => carouselContext?.currentItem,
  () => {
    if (carouselContext?.currentItem === item.value?.index) {
      nextTick(() => scrollTo(0));
      setTimeout(() => {
        isLoaded.value = true;
      }, 1000);
    } else {
      isLoaded.value = false;
    }
  },
);

onMounted(() => {
  carouselContext?.registerItem({
    id: itemId,
    name,
    disabled,
    onFocus: () => emit("focus"),
    handleNextClick,
  });
  if (item.value?.isFirst) {
    setTimeout(() => {
      isLoaded.value = true;
    }, 1000);
  }
});

watch(
  () => disabled,
  () => {
    carouselContext?.updateItem({
      id: itemId,
      name,
      disabled,
      onFocus: () => emit("focus"),
      handleNextClick,
    });
  },
  { immediate: true },
);

const totalSteps = computed(() => carouselContext?.items.length ?? 0);
const currentStep = computed(() => item.value?.index ?? 0);
</script>

<style scoped>
.carousel-form-slide-enter-active,
.carousel-form-slide-leave-active,
.carousel-form-slide-reverse-enter-active,
.carousel-form-slide-reverse-leave-active {
  position: absolute;
  width: 100%;
  -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 98%, rgba(0, 0, 0, 0) 100%);
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 98%, rgba(0, 0, 0, 0) 100%);
}

.carousel-form-slide-enter-active {
  transition:
    transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  transition-delay: 0.1s;
  opacity: 1;
}

.carousel-form-slide-enter-from {
  transform: translateY(100vh);
  opacity: 0;
}

.carousel-form-slide-leave-active {
  transition:
    transform 0.8s cubic-bezier(0.25, 0.6, 0.5, 1),
    opacity 0.8s;
  opacity: 0;
}

.carousel-form-slide-leave-to {
  transform: translateY(-100vh);
}

.carousel-form-slide-reverse-enter-active {
  transition:
    transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 0.4s;
  transition-delay: 0.2s;
  opacity: 1;
}

.carousel-form-slide-reverse-enter-from {
  transform: translateY(-110vh);
  opacity: 0;
}

.carousel-form-slide-reverse-leave-active {
  transition:
    transform 1.4s cubic-bezier(0.1, 0.3, 0.4, 1),
    opacity 0.4s;
  opacity: 0;
}

.carousel-form-slide-reverse-leave-to {
  transform: translateY(100vh);
  opacity: 0;
}
</style>
